// ============================================================
// PinLoginPage — 員工頭像選擇 + 4 位數 PIN 登入
// ============================================================
// 流程：
//   1. 自動拉店鋪所有員工頭像
//   2. 員工點自己頭像 → 進 PIN 鍵盤
//   3. 輸入完 4 位數自動送出
//   4. 成功 → 進 /my (STAFF) 或 /dashboard (OWNER/MANAGER)
//
// 簡化：
//   - 沒設 PIN 的員工灰色 + 標「未設定」
//   - 記住「最近選的員工」localStorage，下次優先顯示
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';
import { BrandMark } from './BrandMark';

interface StaffEntry {
  id: string;
  name: string;
  avatar: string | null;
  role: string;
  hasPin: boolean;
}

const DEFAULT_TENANT_SLUG = 'tianshui';
const LS_LAST_USER = 'tianshui_last_pin_user';

export function PinLoginPage() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<StaffEntry[]>([]);
  const [tenantName, setTenantName] = useState('');
  const [selectedUser, setSelectedUser] = useState<StaffEntry | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // 拉員工列表
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<{ tenant: { name: string }; staff: StaffEntry[] }>(
          `/pin-auth/staff?slug=${DEFAULT_TENANT_SLUG}`
        );
        if (cancelled) return;
        setTenantName(r.tenant.name);
        // 排序：有 PIN 的在前，最近選的更前
        const lastId = localStorage.getItem(LS_LAST_USER);
        const sorted = [...r.staff].sort((a, b) => {
          if (a.id === lastId) return -1;
          if (b.id === lastId) return 1;
          if (a.hasPin !== b.hasPin) return a.hasPin ? -1 : 1;
          return 0;
        });
        setStaff(sorted);
        // auto-redirect-no-pin: 全員都沒設 PIN → 自動跳密碼登入頁
        if (sorted.length > 0 && sorted.every(s => !s.hasPin)) {
          navigate('/login', { replace: true });
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 監聽 PIN 輸入完成自動送出
  const submit = useCallback(async (input: string, user: StaffEntry) => {
    setSubmitting(true);
    setError(null);
    try {
      const data = await api.post<{
        token: string;
        user: { id: string; name: string; email: string; role: string; avatar: string | null };
        tenant: { id: string; name: string; slug: string };
      }>('/pin-auth/login', { userId: user.id, pin: input });

      // 寫入 auth store（與一般 login 同流程）
      api.setToken(data.token);
      localStorage.setItem('ts_user', JSON.stringify(data.user));
      localStorage.setItem('ts_tenant', JSON.stringify(data.tenant));
      useAuthStore.setState({
        user: data.user as any,
        tenant: data.tenant,
        error: null,
      });
      localStorage.setItem(LS_LAST_USER, user.id);
      navigate(data.user.role === 'STAFF' ? '/my' : '/dashboard');
    } catch (err) {
      setError((err as Error).message);
      setPin('');
    } finally {
      setSubmitting(false);
    }
  }, [navigate]);

  // 滿 4 碼後延遲送出；若使用者繼續輸入第 5、6 碼，這次送出會被取消。
  //
  // 原本是「滿 4 碼立刻送出」，但後端允許 4–6 碼（/^\d{4,6}$/）。
  // 主管若替員工設了 5 或 6 碼的 PIN，那位員工永遠登不進去——
  // 前 4 碼就被送出、判定錯誤、欄位清空，重試又是同樣結果，
  // 連續幾次還會觸發鎖定。
  useEffect(() => {
    if (pin.length < 4 || !selectedUser || submitting) return;
    const timer = setTimeout(() => submit(pin, selectedUser), 700);
    return () => clearTimeout(timer);
  }, [pin, selectedUser, submit, submitting]);

  const onDigit = (d: string) => {
    if (pin.length >= 6 || submitting) return;
    setPin(p => p + d);
  };
  const onBackspace = () => setPin(p => p.slice(0, -1));
  const onClear = () => setPin('');

  // ─── 員工頭像顏色 ─────────────
  const avatarBg = (idx: number) => {
    const colors = [
      'bg-earth-200 text-earth-700',
      'bg-sage-200 text-sage-700',
      'bg-smoke-200 text-smoke-700',
      'bg-mist-200 text-mist-700',
      'bg-rose-200 text-rose-700',
      'bg-amber-200 text-amber-700',
    ];
    return colors[idx % colors.length];
  };

  // ─── render ───────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-morandi-50">
        <div className="text-morandi-400">載入中...</div>
      </div>
    );
  }

  // ─── PIN 輸入畫面 ─────────────
  if (selectedUser) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-morandi-50 p-6">
        <div className="w-full max-w-xs flex flex-col items-center">
          <button
            onClick={() => { setSelectedUser(null); setPin(''); setError(null); }}
            className="self-start text-sm text-morandi-400 hover:text-morandi-600 mb-4"
          >
            ‹ 返回
          </button>

          <div className={`w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mb-3 ${avatarBg(staff.indexOf(selectedUser))}`}>
            {selectedUser.name.slice(0, 1)}
          </div>
          <div className="text-lg font-semibold text-morandi-800">{selectedUser.name}</div>
          <div className="text-xs text-morandi-400 mb-6">輸入 PIN 碼</div>

          {/* PIN 顯示點點 */}
          <div className="flex gap-3 mb-2">
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full transition-colors ${
                  i < pin.length ? 'bg-morandi-700' : 'bg-morandi-200'
                }`}
              />
            ))}
            {pin.length > 4 && Array.from({ length: pin.length - 4 }).map((_, i) => (
              <div key={`extra-${i}`} className="w-3 h-3 rounded-full bg-morandi-700" />
            ))}
          </div>

          {/* 5–6 碼的使用者可以直接按確認，不必等自動送出 */}
          {pin.length >= 4 && !submitting && (
            <button
              onClick={() => selectedUser && submit(pin, selectedUser)}
              className="mb-2 px-6 py-1.5 rounded-full bg-sage-500 text-white text-sm font-medium hover:bg-sage-600 active:scale-95 transition-all"
            >
              確認
            </button>
          )}

          {error && (
            <div className="text-sm text-rose-500 mb-3 animate-slide-up">{error}</div>
          )}
          {submitting && (
            <div className="text-sm text-morandi-400 mb-3">驗證中...</div>
          )}

          {/* 數字鍵盤 */}
          <div className="grid grid-cols-3 gap-3 mt-4 w-full">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
              <button
                key={n}
                onClick={() => onDigit(String(n))}
                disabled={submitting}
                className="aspect-square bg-white hover:bg-morandi-100 active:scale-95 rounded-2xl text-2xl font-light text-morandi-700 shadow-card transition-all disabled:opacity-50"
              >
                {n}
              </button>
            ))}
            <button
              onClick={onClear}
              disabled={submitting}
              className="aspect-square text-sm text-morandi-400 hover:text-morandi-600 active:scale-95 disabled:opacity-50"
            >
              清除
            </button>
            <button
              onClick={() => onDigit('0')}
              disabled={submitting}
              className="aspect-square bg-white hover:bg-morandi-100 active:scale-95 rounded-2xl text-2xl font-light text-morandi-700 shadow-card transition-all disabled:opacity-50"
            >
              0
            </button>
            <button
              onClick={onBackspace}
              disabled={submitting}
              className="aspect-square text-xl text-morandi-400 hover:text-morandi-600 active:scale-95 disabled:opacity-50"
            >
              ⌫
            </button>
          </div>

          <div className="mt-6 text-xs text-morandi-400">
            忘記 PIN？
            <Link to="/login" className="text-earth-500 hover:text-earth-700 ml-1">改用密碼登入</Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── 員工選擇畫面 ─────────────
  const withPin = staff.filter(s => s.hasPin);
  const withoutPin = staff.filter(s => !s.hasPin);

  return (
    <div className="min-h-screen flex flex-col items-center bg-morandi-50 p-6 pt-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {/* CIS Lockup 02 直式組合；四周留白即淨空區 */}
          <div className="flex justify-center py-2 mb-1">
            <BrandMark variant="lockup" tone="black" height={104} />
          </div>
          <h1 className="text-base font-medium text-morandi-600 mt-2">{tenantName || '天水豆腐店'}</h1>
          <p className="text-xs text-morandi-400 mt-1">點選你的頭像登入</p>
        </div>

        {staff.length === 0 ? (
          <div className="text-center text-morandi-400 mt-10">
            找不到員工。請聯絡主管或
            <Link to="/login" className="text-earth-500 ml-1">改用密碼登入</Link>
          </div>
        ) : (
          <>
            {/* 已設 PIN 的員工 */}
            {withPin.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-6">
                {withPin.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => { setSelectedUser(s); setPin(''); setError(null); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-white hover:bg-morandi-100 active:scale-95 transition-all shadow-card"
                  >
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold ${avatarBg(i)}`}>
                      {s.name.slice(0, 1)}
                    </div>
                    <div className="text-sm text-morandi-700 font-medium">{s.name}</div>
                  </button>
                ))}
              </div>
            )}

            {/* 未設 PIN 的員工 */}
            {withoutPin.length > 0 && (
              <div className="border-t border-morandi-200 pt-4">
                <div className="text-xs text-morandi-400 mb-2 text-center">
                  以下員工尚未設定 PIN（請改用密碼登入後到「密碼與安全」設定）
                </div>
                <div className="grid grid-cols-3 gap-3 opacity-50">
                  {withoutPin.map((s, i) => (
                    <div
                      key={s.id}
                      className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-morandi-100"
                    >
                      <div className="w-12 h-12 rounded-full bg-morandi-200 flex items-center justify-center text-base font-bold text-morandi-500">
                        {s.name.slice(0, 1)}
                      </div>
                      <div className="text-xs text-morandi-500">{s.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className="text-center mt-8">
          <Link to="/login" className="text-xs text-morandi-400 hover:text-morandi-600">
            改用 Email + 密碼登入
          </Link>
        </div>
      </div>
    </div>
  );
}
